import { z } from 'zod';
import superjson from 'superjson';
import { ChatAttachmentItem } from './list_GET.schema';

const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB — supports large GLB/3D models, video, etc.

export const schema = z.object({
  sessionId: z.string().min(1),
  fileName: z.string().min(1),
  fileType: z.string().optional(), // Any file type allowed
  fileSize: z.number().max(MAX_FILE_SIZE).optional(),
  dataUrl: z.string().min(1),
});

export type InputType = z.infer<typeof schema>;

export type OutputType = {
  attachment: ChatAttachmentItem;
};

export const uploadChatAttachment = async (
  body: InputType,
  init?: RequestInit,
): Promise<OutputType> => {
  const validatedInput = schema.parse(body);
  const result = await fetch(`/_api/chat-attachments/upload`, {
    method: 'POST',
    credentials: 'include',
    body: superjson.stringify(validatedInput),
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
  });
  if (!result.ok) {
    const raw = await result.text();
    if (raw.trimStart().startsWith('<!DOCTYPE') || raw.trimStart().startsWith('<html')) {
      throw new Error(result.status === 503 ? 'Server temporarily unavailable — file may be too large. Try again.' : `Upload failed (${result.status})`);
    }
    try {
      const errorObject = superjson.parse<{ error: string }>(raw);
      throw new Error(errorObject.error);
    } catch (e) {
      if (e instanceof Error && !e.message.includes('Unexpected token')) throw e;
      throw new Error(`Upload failed (${result.status})`);
    }
  }
  const raw = await result.text();
  try {
    return superjson.parse<OutputType>(raw);
  } catch {
    throw new Error('Invalid response from server');
  }
};
